import React, {Component, ReactElement} from 'react'
import NatRoomToResidentRepo from '../roomToResident/repository/NatRoomToResidentRepo'
import NatRidgeRepo from '../ridge/repository/NatRidgeRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {NatGetRidge, RidgeData} from '../ridge/repository/NatGetRidge'
import {
    NatGetResidentUserStatusListRequest,
    NatGetResidentUserStatusListResponse,
    ResidentUserInfo,
} from '../roomToResident/repository/NatGetResidentUserStatusConfirmation'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {
    RESIDENT_STATUS,
    RESIDENT_USER_TYPE,
    ROLE_FOR_RESIDENT,
    VALID_OR_NOT,
    VALID_OR_NOT_CHECK,
} from '../../../CodeMaster'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import {SortOrder} from '../../../components/shared/SortOrder'
import {SearchResultCountDisplayProps} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import {Messages} from '../../../Messages'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper,
} from '../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import ListSearchFormArea from '../../../styledComponents/adminPage/ListSearchFormArea'
import {
    ConditionButtonCell,
    ConditionCategoryCell,
    ConditionColumNameCell,
    ConditionInputCell,
    ConditionTable,
    ConditionTableBody,
    ConditionTableRow,
} from '../../../styledComponents/adminPage/ConditionTable'
import PPSelect from '../../../components/shared/forms/PPSelect'
import {addEmptyData} from '../../../componentHelper/selectHelper'
import PPTextField from '../../../components/shared/forms/PPTextField'
import PPCheckboxGroup from '../../../components/shared/forms/PPCheckboxGroup'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTable from '../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import {SortDirection} from '@mui/material/TableCell'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {HKConstants} from '../../../HKConstants'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Box, TableCell, TableRow} from '@mui/material'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import {getStringOrEmptyString} from '../../common/util/Natutil'
import {TransitionDestinationInfo} from './admin/NatTransitionDestinationInterface'
import {flipSortDirection} from '../../../components/shared/SortDirectionHelper'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

const KEY_RESIDENT_USER_STATUS_LIST_SEARCH_CONDITION: string = 'adminResidentUserStatusListPageSearchCondition'

interface Props {
    roomToResidentRepo: NatRoomToResidentRepo
    ridgeRepo: NatRidgeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    ridgeInfo: RidgeData[]
    ridgeCd: string
    roomNumber: string
    roleId: CheckBox[]
    residentUserType: CheckBox[]
    assocDirectorFlag: CheckBox[]
    residentStatus: CheckBox[]
    assocUnassignedFlag: CheckBox[]
    sortInfo: SortOrder[]
    total: number
    page: number
    pageNum: number
    residentUserStatusConfirmationRequest: NatGetResidentUserStatusListRequest
    residentUserStatusInfoList: ResidentUserInfo[]
    isLoadCompleted: boolean
}

interface LastSearchCondition {
    ridgeCd?: string
    roomNumber?: string
    roleId?: string[]
    residentUserType?: string[]
    assocDirectorFlag?: string[]
    residentStatus?: string[]
    assocUnassignedFlag?: string[]
    pageNum?: number
}

interface CheckBox {
    label: string
    value: string
    checked: boolean
}

class NatAdminResidentUserStatusConfirmationPage extends Component<Props & AdminPagePropsSet, States> {
    lastSearchCondition: LastSearchCondition
    selectedCondominiumCd: Nullable<string>
    transitionDestinationInfo: TransitionDestinationInfo

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.lastSearchCondition = this.getLastSearchCondition()
        this.selectedCondominiumCd = NatAdminLoginInfoSessionStorageHelper.getStringItemOrNull(
            AdminLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_CD)
        this.transitionDestinationInfo = {
            label: '居住者ユーザ状況確認へ戻る',
            url: '/admin/residentUserStatusConfirmation',
        }

        const roleId = this.getLastSearchConditionOrNull('roleId')
        const residentUserType = this.getLastSearchConditionOrNull('residentUserType')
        const assocDirectorFlag = this.getLastSearchConditionOrNull('assocDirectorFlag')
        const residentStatus = this.getLastSearchConditionOrNull('residentStatus')
        const assocUnassignedFlag = this.getLastSearchConditionOrNull('assocUnassignedFlag')

        const defaultSearchConditionRidgeCd = this.getLastSearchConditionOrNull('ridgeCd') || ''
        const defaultSearchConditionRoomNumber = this.getLastSearchConditionOrNull('roomNumber') || ''
        const defaultSearchConditionRoleId = ROLE_FOR_RESIDENT.values()
            .map(codeMaster => ({
                label: codeMaster.label,
                value: codeMaster.value,
                checked: roleId ? roleId.includes(codeMaster.value) : true,
            }))
        const defaultSearchConditionUserType: CheckBox[] = RESIDENT_USER_TYPE.values()
            .map(codeMaster => ({
                label: codeMaster.label,
                value: codeMaster.value,
                checked: residentUserType ? residentUserType.includes(codeMaster.value) : true,
            }))
        const defaultSearchConditionAssoc = VALID_OR_NOT.values()
            .map(codeMaster => ({
                label: codeMaster.label,
                value: codeMaster.value,
                checked: assocDirectorFlag ? assocDirectorFlag.includes(codeMaster.value) : true,
            }))
        const defaultSearchConditionResidentStatus = RESIDENT_STATUS.values()
            .filter(codeMaster => codeMaster !== RESIDENT_STATUS.HASEKO_WITHDRAW)
            .map(codeMaster => ({
                label: codeMaster.label,
                value: codeMaster.value,
                checked: residentStatus ? residentStatus.includes(codeMaster.value) : true,
            }))
        const defaultSearchConditionAssocUnassigned = [{
            label: '未設定の部屋番号のみ表示',
            value: VALID_OR_NOT.VALID.value,
            checked: assocUnassignedFlag ? assocUnassignedFlag.includes(VALID_OR_NOT.VALID.value) : false,
        }]
        const defaultSearchConditionPageNum = this.getLastSearchConditionOrNull('pageNum') ||
            COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2]
        const sortInfo: SortOrder[] = [new SortOrder('ridgeName', 'asc'), new SortOrder('roomNumber', 'asc')]

        this.state = {
            ridgeInfo: [],
            ridgeCd: defaultSearchConditionRidgeCd,
            roomNumber: defaultSearchConditionRoomNumber,
            roleId: defaultSearchConditionRoleId,
            residentUserType: defaultSearchConditionUserType,
            assocDirectorFlag: defaultSearchConditionAssoc,
            residentStatus: defaultSearchConditionResidentStatus,
            assocUnassignedFlag: defaultSearchConditionAssocUnassigned,
            sortInfo: sortInfo,
            total: 0,
            page: COMMON_CONSTANTS.FIRST_PAGE,
            pageNum: defaultSearchConditionPageNum,
            residentUserStatusConfirmationRequest: {
                ridgeCd: defaultSearchConditionRidgeCd,
                roomNumber: defaultSearchConditionRoomNumber,
                roleId: defaultSearchConditionRoleId
                    .filter((check) => check.checked)
                    .map((check) => check.value),
                residentUserType: defaultSearchConditionUserType
                    .filter((check) => check.checked)
                    .map((check) => check.value),
                assocDirectorFlag: defaultSearchConditionAssoc
                    .filter((check) => check.checked)
                    .map((check) => check.value),
                status: defaultSearchConditionResidentStatus
                    .filter((check) => check.checked)
                    .map((check) => check.value),
                assocUnassignedFlag: defaultSearchConditionAssocUnassigned
                    .filter((check) => check.checked)
                    .map((check) => check.value),
                pageNum: defaultSearchConditionPageNum,
                sortInfo: sortInfo,
                page: COMMON_CONSTANTS.FIRST_PAGE,
            },
            residentUserStatusInfoList: [],
            isLoadCompleted: false,
        }

        this.onChangeRidgeCd = this.onChangeRidgeCd.bind(this)
        this.onChangeRoomNumber = this.onChangeRoomNumber.bind(this)
        this.onChangeUserType = this.onChangeUserType.bind(this)
        this.onChangeAssocDirector = this.onChangeAssocDirector.bind(this)
        this.onChangeStatus = this.onChangeStatus.bind(this)
        this.onChangeAssocUnassigned = this.onChangeAssocUnassigned.bind(this)
        this.onChangeRole = this.onChangeRole.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleSortMessages = this.handleSortMessages.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await Promise.all([
                this.getRidge(),
                this.getResidentUserStatusList(this.state.residentUserStatusConfirmationRequest),
            ])
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    saveLastSearchCondition(lastSearchCondition: LastSearchCondition): void {
        SessionStorageHelper.setItem(
            KEY_RESIDENT_USER_STATUS_LIST_SEARCH_CONDITION,
            JSON.stringify(lastSearchCondition))
    }

    getLastSearchCondition(): LastSearchCondition {
        let lastSearchCondition: LastSearchCondition = {}
        const sessionStorageSearchCondition: string | null =
            SessionStorageHelper.getStringItemOrNull(KEY_RESIDENT_USER_STATUS_LIST_SEARCH_CONDITION)
        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        }
        return lastSearchCondition
    }

    getLastSearchConditionOrNull<U extends keyof LastSearchCondition>(key: U): Nullable<LastSearchCondition[U]> {
        if (this.lastSearchCondition[key] !== undefined) {
            return this.lastSearchCondition[key]
        }
        return null
    }

    async getRidge(): Promise<void> {
        const result: Nullable<NatGetRidge> = await this.props.asyncCallFunction(
            this.props.ridgeRepo.getRidgeList.bind(this.props.ridgeRepo, this.selectedCondominiumCd ?
                this.selectedCondominiumCd : undefined),
            (httpErrorOrNetworkError) => {
                this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        if (result && result.details) {
            this.setState({ridgeInfo: result.details})
        }
    }

    async getResidentUserStatusList(request: NatGetResidentUserStatusListRequest): Promise<void> {
        const result: Nullable<NatGetResidentUserStatusListResponse> =
            await this.props.asyncCallFunction(
                this.props.roomToResidentRepo.getRoomToResidentStatus.bind(
                    this.props.roomToResidentRepo,
                    request,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        if (result && result.details) {
            this.setState({
                residentUserStatusInfoList: result.details.residentUserInfo,
                total: result.details.searchResult.total,
            })
        }
    }

    async searchResidentUserStatusList(request: NatGetResidentUserStatusListRequest): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getResidentUserStatusList(request)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    onChangeRidgeCd(event: SelectChangeEvent<unknown>): void {
        let ridgeCd: string = event.target.value as string
        if (ridgeCd === 'none') {
            ridgeCd = ''
        }
        this.setState({ridgeCd: ridgeCd})
    }

    onChangeRoomNumber(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({roomNumber: event.target.value})
    }

    onChangeRole(label: string, checked: boolean): void {
        this.setState({
            roleId: this.state.roleId.map(check => ({
                label: check.label,
                value: check.value,
                checked: (check.label === label) ?
                    checked : check.checked,
            })),
        })
    }

    onChangeUserType(label: string, checked: boolean): void {
        this.setState({
            residentUserType: this.state.residentUserType.map(check => ({
                label: check.label,
                value: check.value,
                checked: (check.label === label) ?
                    checked : check.checked,
            })),
        })
    }

    onChangeAssocDirector(label: string, checked: boolean): void {
        this.setState({
            assocDirectorFlag: this.state.assocDirectorFlag.map(check => ({
                label: check.label,
                value: check.value,
                checked: (check.label === label) ?
                    checked : check.checked,
            })),
        })
    }

    onChangeStatus(label: string, checked: boolean): void {
        this.setState({
            residentStatus: this.state.residentStatus.map(check => ({
                label: check.label,
                value: check.value,
                checked: (check.label === label) ?
                    checked : check.checked,
            })),
        })
    }

    onChangeAssocUnassigned(label: string, checked: boolean): void {
        this.setState({
            assocUnassignedFlag: this.state.assocUnassignedFlag.map(check => ({
                label: check.label,
                value: check.value,
                checked: (check.label === label) ?
                    checked : check.checked,
            })),
        })
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()

        const residentUserStatusConfirmationRequest = this.state.residentUserStatusConfirmationRequest
        residentUserStatusConfirmationRequest.page = newPage

        await this.searchResidentUserStatusList(residentUserStatusConfirmationRequest)

        this.setState({
            residentUserStatusConfirmationRequest: residentUserStatusConfirmationRequest,
            page: newPage,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
        })
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        const residentUserStatusConfirmationRequest = this.state.residentUserStatusConfirmationRequest
        residentUserStatusConfirmationRequest.pageNum = Number(event.target.value)
        residentUserStatusConfirmationRequest.page = COMMON_CONSTANTS.FIRST_PAGE

        await this.searchResidentUserStatusList(residentUserStatusConfirmationRequest)

        this.setState({
            residentUserStatusConfirmationRequest: residentUserStatusConfirmationRequest,
            pageNum: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
            this.saveLastSearchCondition({
                ridgeCd: residentUserStatusConfirmationRequest.ridgeCd,
                roomNumber: residentUserStatusConfirmationRequest.roomNumber,
                roleId: residentUserStatusConfirmationRequest.roleId,
                residentUserType: residentUserStatusConfirmationRequest.residentUserType,
                assocDirectorFlag: residentUserStatusConfirmationRequest.assocDirectorFlag,
                residentStatus: residentUserStatusConfirmationRequest.status,
                assocUnassignedFlag: residentUserStatusConfirmationRequest.assocUnassignedFlag,
                pageNum: residentUserStatusConfirmationRequest.pageNum,
            })
        })
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()

        const residentUserStatusConfirmationRequest = this.state.residentUserStatusConfirmationRequest
        residentUserStatusConfirmationRequest.ridgeCd = this.state.ridgeCd
        residentUserStatusConfirmationRequest.roomNumber = this.state.roomNumber
        residentUserStatusConfirmationRequest.roleId = this.state.roleId
            .filter((check) => check.checked)
            .map((check) => check.value)
        residentUserStatusConfirmationRequest.residentUserType = this.state.residentUserType
            .filter((check) => check.checked)
            .map((check) => check.value)
        residentUserStatusConfirmationRequest.assocDirectorFlag = this.state.assocDirectorFlag
            .filter((check) => check.checked)
            .map((check) => check.value)
        residentUserStatusConfirmationRequest.status = this.state.residentStatus
            .filter((check) => check.checked)
            .map((check) => check.value)
        residentUserStatusConfirmationRequest.assocUnassignedFlag = this.state.assocUnassignedFlag
            .filter((check) => check.checked)
            .map((check) => check.value)
        residentUserStatusConfirmationRequest.page = COMMON_CONSTANTS.FIRST_PAGE

        await this.searchResidentUserStatusList(residentUserStatusConfirmationRequest)

        this.setState({
            residentUserStatusConfirmationRequest: residentUserStatusConfirmationRequest,
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, () => {
            this.props.eventEmitter.scrollToContentsTop()
            this.saveLastSearchCondition({
                ridgeCd: this.state.ridgeCd,
                roomNumber: residentUserStatusConfirmationRequest.roomNumber,
                roleId: residentUserStatusConfirmationRequest.roleId,
                residentUserType: residentUserStatusConfirmationRequest.residentUserType,
                assocDirectorFlag: residentUserStatusConfirmationRequest.assocDirectorFlag,
                residentStatus: residentUserStatusConfirmationRequest.status,
                assocUnassignedFlag: residentUserStatusConfirmationRequest.assocUnassignedFlag,
                pageNum: residentUserStatusConfirmationRequest.pageNum,
            })
        })
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.residentUserStatusInfoList.length === 0) {
            return {noResultMessage: Messages.IOPCC0014.toString('')}
        }

        return {
            totalCount: this.state.total,
            rowsPerPage: this.state.pageNum,
            page: this.state.page,
        }
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.sortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    async handleSortMessages(sortKey: string): Promise<void> {
        this.beforeUserAction()

        const sortInfo: SortOrder[] = [
            new SortOrder(
                'ridgeName',
                flipSortDirection(this.getSortDirection('ridgeName'), 'desc'),
            ),
            new SortOrder(
                'roomNumber',
                flipSortDirection(this.getSortDirection('roomNumber'), 'desc'),
            ),
        ]

        const residentUserStatusConfirmationRequest = this.state.residentUserStatusConfirmationRequest
        residentUserStatusConfirmationRequest.sortInfo = sortInfo
        residentUserStatusConfirmationRequest.page = COMMON_CONSTANTS.FIRST_PAGE

        await this.searchResidentUserStatusList(residentUserStatusConfirmationRequest)

        this.setState({
            residentUserStatusConfirmationRequest: residentUserStatusConfirmationRequest,
            page: COMMON_CONSTANTS.FIRST_PAGE,
            sortInfo: sortInfo,
        })
    }

    renderResidentUserTable(): Nullable<ReactElement> {
        if (this.state.residentUserStatusInfoList.length === 0) {
            return null
        }
        const headers: SortableTableHeaderCell[] = [
            {id: 'roomNumber', label: '部屋番号', sortable: true, direction: this.getSortDirection('roomNumber')},
            {id: 'adminName', label: '管理者確認用氏名'},
            {id: 'role', label: '権限'},
            {id: 'residentUserType', label: 'ユーザタイプ'},
            {id: 'assocDirector', label: '管理組合有効役員'},
            {id: 'status', label: 'ステータス'},
            {id: 'validPeriodDate', label: '有効期間'},
            {id: 'updateDatetime', label: '更新日時'},
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.total,
                    rowsPerPage: this.state.pageNum,
                    page: this.state.page,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    headers={headers}
                    onSortOrderChange={this.handleSortMessages}
                />
                <SortablePaginationTableBody>
                    {this.state.residentUserStatusInfoList.map(
                        (residentUserStatusInfo) =>
                            this.renderResidentUserStatusRecord(residentUserStatusInfo),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderResidentUserStatusRecord(residentUserStatusInfo: ResidentUserInfo): ReactElement {
        const {
            seq, condominiumCd, ridgeCd, ridgeName,
            roomNumber, adminFamilyName, adminFirstName,
            roleName, residentUserType, assocDirectorFlag, status,
            validPeriodStartDate, validPeriodEndDate, updateDatetime,
        } = residentUserStatusInfo

        const validPeriodDate: string = (validPeriodStartDate || validPeriodEndDate) ?
            `${getStringOrEmptyString(validPeriodStartDate)} ～ ${getStringOrEmptyString(validPeriodEndDate)}` : ''

        return (
            <TableRow key={`${condominiumCd}_${ridgeCd}_${roomNumber}_${seq}`}>
                <TableCellWordBreakAll align={'center'}>
                    <StyledLink onClick={() =>
                        this.props.navigate(`/admin/residentUser/${condominiumCd}/${ridgeCd}/${roomNumber}/${seq}`,
                            {state: {returnLinkInfo: this.transitionDestinationInfo}})}
                    >
                        {ridgeName} {roomNumber}
                    </StyledLink>
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {adminFamilyName} {adminFirstName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {roleName}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {RESIDENT_USER_TYPE.codeToEnumConstants(residentUserType).label}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {VALID_OR_NOT_CHECK.codeToEnumConstants(assocDirectorFlag).label}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {RESIDENT_STATUS.codeToEnumConstants(status).label}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {validPeriodDate}
                </TableCellWordBreakAll>
                <TableCellWordBreakAll align={'center'}>
                    {formatDateTimeForUpdateDate(updateDatetime)}
                </TableCellWordBreakAll>
            </TableRow>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <ListSearchFormArea
                    searchResult={this.searchResults()}
                    onEnterKey={this.onClickSearchButton}
                >
                    <ConditionTable>
                        <ConditionTableBody>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={2}>物件情報</ConditionCategoryCell>
                                <ConditionColumNameCell>棟名</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPSelect
                                        fullWidth
                                        data={addEmptyData(this.state.ridgeInfo.map((ridge) => {
                                            return ({
                                                label: `${ridge.ridgeCd} : ${ridge.ridgeName}`,
                                                value: `${ridge.ridgeCd}`,
                                            })
                                        }))}
                                        onChange={this.onChangeRidgeCd}
                                        value={this.state.ridgeCd}
                                        autoFocus
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>部屋番号</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPTextField
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 4,
                                            },
                                        }}
                                        fullWidth
                                        onChange={this.onChangeRoomNumber}
                                        defaultValue={this.state.roomNumber}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell></ConditionButtonCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionCategoryCell rowSpan={4}>居住者情報</ConditionCategoryCell>
                                <ConditionColumNameCell>権限</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            this.state.roleId.map(check => ({
                                                label: check.label,
                                                checked: check.checked,
                                            }))
                                        }
                                        onChangeChecked={this.onChangeRole}
                                    />
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>ユーザタイプ</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            this.state.residentUserType.map(check => ({
                                                label: check.label,
                                                checked: check.checked,
                                            }))
                                        }
                                        onChangeChecked={this.onChangeUserType}
                                    />
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>管理組合役員</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            this.state.assocDirectorFlag.map(check => ({
                                                label: check.label,
                                                checked: check.checked,
                                            }))
                                        }
                                        onChangeChecked={this.onChangeAssocDirector}
                                    />
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionColumNameCell>ステータス</ConditionColumNameCell>
                                <ConditionInputCell colSpan={2}>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            this.state.residentStatus.map(check => ({
                                                label: check.label,
                                                checked: check.checked,
                                            }))
                                        }
                                        onChangeChecked={this.onChangeStatus}
                                    />
                                </ConditionInputCell>
                            </ConditionTableRow>
                            <ConditionTableRow>
                                <ConditionCategoryCell>その他</ConditionCategoryCell>
                                <ConditionColumNameCell>組合コード</ConditionColumNameCell>
                                <ConditionInputCell>
                                    <PPCheckboxGroup
                                        row
                                        data={
                                            this.state.assocUnassignedFlag.map(check => ({
                                                label: check.label,
                                                checked: check.checked,
                                            }))
                                        }
                                        onChangeChecked={this.onChangeAssocUnassigned}
                                    />
                                </ConditionInputCell>
                                <ConditionButtonCell>
                                    <AdminDefaultMediumButton
                                        onClick={this.onClickSearchButton}>
                                        居住者ユーザを検索する
                                    </AdminDefaultMediumButton>
                                </ConditionButtonCell>
                            </ConditionTableRow>
                        </ConditionTableBody>
                    </ConditionTable>
                </ListSearchFormArea>
                <SearchPageActionArea>
                    <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.EDIT_RESIDENT_USER}>
                        <LeftRowFlexContainer>
                            <AdminDefaultMediumButton onClick={() =>
                                this.props.navigate('/admin/residentUserRegister',
                                    {state: {returnLinkInfo: this.transitionDestinationInfo}})}
                            >
                                新規
                            </AdminDefaultMediumButton>
                            <Box ml={'15px'}>
                                <AdminDefaultMediumButton onClick={() =>
                                    this.props.navigate('/admin/bulkUpdate/residentUser',
                                        {state: {returnLinkInfo: this.transitionDestinationInfo}})}
                                >
                                    一括更新
                                </AdminDefaultMediumButton>
                            </Box>
                        </LeftRowFlexContainer>
                    </AuthorityChecker>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderResidentUserTable()}
                </PageMainArea>
            </>
        )
    }
}

export default createAdminPage(NatAdminResidentUserStatusConfirmationPage)

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`
